import { useState, useRef } from 'react';
import { ScrollText, Trash2, AlertCircle, CheckCircle2, Info, AlertTriangle } from 'lucide-react';
import { useLogStore, type LogEntry } from '@/store/useLogStore';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

const levelConfig: Record<LogEntry['level'], { icon: typeof Info; color: string; bg: string; label: string }> = {
    info: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-50', label: 'INFO' },
    warn: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50', label: 'WARN' },
    error: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'ERROR' },
    success: { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-50', label: 'OK' },
};

export function LogPage() {
    const { logs, clearLogs } = useLogStore();
    const [filter, setFilter] = useState<LogEntry['level'] | 'all'>('all');
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    const filteredLogs = filter === 'all' ? logs : logs.filter(l => l.level === filter);

    // 自动滚动到底部（新日志在顶部，所以不需要滚动）
    const counts = {
        info: logs.filter(l => l.level === 'info').length,
        warn: logs.filter(l => l.level === 'warn').length,
        error: logs.filter(l => l.level === 'error').length,
        success: logs.filter(l => l.level === 'success').length,
    };

    return (
        <div className="flex h-full flex-col">
            <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-4 py-3">
                <div>
                    <h1 className="text-base font-semibold text-zinc-900">运行日志</h1>
                    <p className="text-xs text-zinc-500">查看批量任务和系统操作的实时日志</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={clearLogs} disabled={logs.length === 0}>
                        <Trash2 size={14} />
                        清空
                    </Button>
                </div>
            </div>

            {/* 过滤栏 */}
            <div className="flex shrink-0 items-center gap-1.5 border-b border-zinc-100 px-4 py-2">
                <span className="text-xs text-zinc-400 mr-1">筛选:</span>
                {(['all', 'info', 'success', 'warn', 'error'] as const).map(lv => {
                    const isActive = filter === lv;
                    const count = lv === 'all' ? logs.length : counts[lv];
                    return (
                        <button
                            key={lv}
                            onClick={() => setFilter(lv)}
                            className={cn(
                                'rounded-md px-2 py-0.5 text-xs font-medium transition-colors',
                                isActive ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200',
                            )}
                        >
                            {lv === 'all' ? '全部' : levelConfig[lv].label}
                            {count > 0 && <span className="ml-1 opacity-70">{count}</span>}
                        </button>
                    );
                })}
                <span className="ml-auto text-xs text-zinc-400">共 {filteredLogs.length} 条</span>
            </div>

            {/* 日志列表 */}
            <div ref={scrollRef} className="flex-1 overflow-auto">
                {filteredLogs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-zinc-100">
                            <ScrollText size={24} className="text-zinc-300" />
                        </div>
                        <p className="text-sm text-zinc-400">暂无日志记录</p>
                        <p className="text-xs text-zinc-300">执行批量操作后日志会自动记录在这里</p>
                    </div>
                ) : (
                    <div className="divide-y divide-zinc-100">
                        {filteredLogs.map(log => {
                            const cfg = levelConfig[log.level];
                            const Icon = cfg.icon;
                            const isExpanded = expandedId === log.id;
                            return (
                                <div key={log.id} className={cn('px-4 py-2 transition-colors', cfg.bg)}>
                                    <div
                                        className="flex items-start gap-2 cursor-pointer"
                                        onClick={() => setExpandedId(isExpanded ? null : log.id)}
                                    >
                                        <Icon size={14} className={cn('mt-0.5 shrink-0', cfg.color)} />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] font-mono text-zinc-400">{formatTime(log.timestamp)}</span>
                                                <span className={cn('rounded px-1 py-0.5 text-[10px] font-semibold', cfg.bg, cfg.color)}>{cfg.label}</span>
                                                <span className="rounded bg-zinc-200/60 px-1 py-0.5 text-[10px] font-medium text-zinc-500">{log.tag}</span>
                                            </div>
                                            <p className="mt-0.5 text-sm text-zinc-800">{log.message}</p>
                                            {log.detail && isExpanded && (
                                                <pre className="mt-1 whitespace-pre-wrap break-all rounded bg-white/60 px-2 py-1.5 text-xs text-zinc-600 font-mono">{log.detail}</pre>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function formatTime(ts: number): string {
    const d = new Date(ts);
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    const ms = String(d.getMilliseconds()).padStart(3, '0');
    return `${h}:${m}:${s}.${ms}`;
}
